/* oxlint-disable no-restricted-syntax */
/* oxlint-disable no-labels */
import { AbortError, cloneDeep } from 'es-toolkit';

import { getIntervalsApi } from '@flipster/apis/v2/interval';
import type { Interval as ApiIntervalData } from '@flipster/apis/v2/interval/types';
import {
  IntervalSnapshotSocketClosedError,
  IntervalSnapshotTimeoutError,
} from '@flipster/r230522/original/interval/error';
import type { RequestIntervalSnapshotFn } from '@flipster/r230522/original/interval/socket';
import type { IntervalDataUpdate as SocketIntervalData } from '@flipster/r230522/original/interval/types';
import NearWorldClock from '@flipster/utils/clock/near-world-clock';
import { datadog, tradeLogger } from '@flipster/utils/datadog';
import deferred, { Deferred } from '@flipster/utils/deferred';
import { EventEmitter } from '@flipster/utils/event-emitter';
import segment, { ChartDataType, ChartEvent } from '@flipster/utils/segment';
import { PerformanceEvent } from '@flipster/utils/segment/events/performance';
import sleep from '@flipster/utils/sleep';
import { startTimeout } from '@flipster/utils/timeout';

import type { Bar } from '../../lib/charting_library';
import { toIntervalTransportPriceType } from '../misc/chart-price-type';
import { secsToResolution, symbolNameToInfo } from '../misc/helper';
import { Period, iterBlocksToLeft, iterBlocksToRight } from './chunks';

export function calcBarCount(period: Period, resolution: number): number {
  return (period.to - period.from) / resolution;
}

function apiIntervalDataToBar({ t, o, c, l, h, m }: ApiIntervalData): Bar {
  return {
    time: t,
    open: Number(o),
    close: Number(c),
    low: Number(l),
    high: Number(h),
    volume: Number(m), // notional volume
  };
}

export function socketIntervalDataToTimestamp(socketIntervalData: SocketIntervalData): number {
  const { b } = socketIntervalData;
  return Number(b.substring(0, 13));
}

export function socketIntervalDataToBar(socketIntervalData: SocketIntervalData): Bar {
  const { o, h, l, c, t } = socketIntervalData;
  return {
    time: socketIntervalDataToTimestamp(socketIntervalData),
    open: Number(o),
    high: Number(h),
    low: Number(l),
    close: Number(c),
    // https://prextech.slack.com/archives/C060T34J6P8/p1710141848102819
    volume: Number(t),
  };
}

export function updateBarWithPartialSocketIntervalData(oldBar: Bar, intervalData: SocketIntervalData): Bar {
  const bar: Partial<Bar> = socketIntervalDataToBar(intervalData);
  const { o, h, l, c, t } = intervalData;
  if (o == null) delete bar.open;
  if (h == null) delete bar.high;
  if (l == null) delete bar.low;
  if (c == null) delete bar.close;
  if (t == null) delete bar.volume;
  return { ...oldBar, ...bar };
}

interface RetryLogContext {
  symbol: string;
  timeframe: string;
  period: Period;
  direction: 'ltr' | 'rtl';
}

interface RetryErrorInfo {
  status: number | undefined;
  code: string | undefined;
  message: string;
}

// getIntervalsApi가 throw한 에러에서 원인 분석에 필요한 정보를 추출함.
// axios가 tvchart의 직접 의존성이 아니므로 AxiosError 형태를 duck-typing으로 읽음.
function extractRetryErrorInfo(error: unknown): RetryErrorInfo {
  if (typeof error === 'object' && error !== null) {
    const e = error as { response?: { status?: number }; code?: string; message?: string };
    return {
      status: e.response?.status, // HTTP 응답이 있는 경우의 status (5xx 등)
      code: e.code, // 네트워크 오류 코드 (ERR_NETWORK, ECONNABORTED 등)
      message: e.message ?? String(error),
    };
  }
  return { status: undefined, code: undefined, message: String(error) };
}

// 무한 재시도 시 로그 폭주를 막기 위해, 첫 실패와 이후 RETRY_LOG_INTERVAL 회마다 한 번씩만 로깅함.
// 재시도 간격이 500ms이므로 10회 ≈ 5초마다 1회 로깅됨.
const RETRY_LOG_INTERVAL = 10;

async function retryUntilSuccess<T>(
  fn: () => Promise<T>,
  logContext: RetryLogContext,
  interval: number = 500,
): Promise<T> {
  let attempt = 0;
  while (true) {
    try {
      return await fn();
    } catch (error) {
      attempt += 1;
      if (attempt === 1 || attempt % RETRY_LOG_INTERVAL === 0) {
        const { status, code, message } = extractRetryErrorInfo(error);
        tradeLogger.warn(
          'chart_api_retry',
          {
            error_message: message,
            // status가 있으면 HTTP 응답 오류(404/429 외 4xx·5xx), 없으면 네트워크/타임아웃 오류
            error_category: status != null ? 'http_errors' : 'network_errors',
            symbol: logContext.symbol,
            timeframe: logContext.timeframe,
            period: logContext.period,
            direction: logContext.direction,
            status,
            code,
            attempt,
            // catch 경로는 SSR에서도 도달 가능하므로 window 접근을 가드함
            url: typeof window !== 'undefined' ? window.location.href : undefined,
          },
          error,
        );
      }
      await sleep(interval);
    }
  }
}

export class Block implements Period {
  constructor(
    public from: number,
    public to: number,
    public bars: Bar[],
    // 200: 상장일 이후의 블록을 받아온 경우. 미래의 블록을 가져온 경우도 포함
    // 404: 상장일 이전의 블록을 받아온 경우. 다만 상장일 이전이어도 상장일과 가까운 경우 200이 내려올 수도 있음
    // 429: 너무 많이 요청한 경우. 어느 시점의 블록을 가져와도 429일 수 있음
    public statusCode: number,
  ) {}

  static toBars(blocks: Block[]): Bar[] {
    return blocks.flatMap((block) => block.bars);
  }

  static async forEachBar(blocks: Block[], fn: (bar: Bar) => Promise<void>): Promise<void> {
    for (const block of blocks) {
      for (const bar of block.bars) {
        await fn(bar);
      }
    }
  }

  static calcPeriod(countBack: number, resolution: number, to: number): Period {
    const from = to - resolution * Math.min(countBack, 1000);
    return { from, to };
  }

  static *iterPeriods({ from, to }: Period, resolution: number, size = 100): Generator<Period> {
    let acc = Math.floor(from / resolution / size) * resolution * size;
    while (acc < to) {
      yield { from: acc, to: acc + resolution * (size - 1) };
      acc += resolution * size;
    }
  }

  static fetch(symbolName: string, resolution: number, period: Period, direction: 'ltr' | 'rtl'): Promise<Block[]> {
    const { symbol, priceType } = symbolNameToInfo(symbolName);
    // Spot MID and perp LAST/MARK are requested as native transport price types.
    const transportPriceType = toIntervalTransportPriceType(priceType);
    const resolutionString = secsToResolution(resolution);

    const periods = direction === 'ltr' ? iterBlocksToRight(period, resolution) : iterBlocksToLeft(period, resolution);

    const blocks = Array.from(periods).map(({ from, to }) =>
      retryUntilSuccess(
        async () => {
          const { status, headers, data, config } = await getIntervalsApi(symbol, resolutionString, from, to, {
            priceType: transportPriceType,
          });

          const eventMap: ChartDataType[typeof ChartEvent.ApiResponsed] = {
            api_url: `${config.baseURL}${config.url}`,
            cf_cache_status: headers['cf-cache-status'] || 'unknown',
            is_last: headers['x-prex-last-data'] === 'true',
          };

          if (typeof performance !== 'undefined') {
            const performanceKey = `/api/v2/chart/klines/${symbol}/${resolutionString}/${from}-${to}?priceType=${transportPriceType}`;
            const entry = performance
              .getEntriesByType('resource')
              .filter((resource) => resource instanceof PerformanceResourceTiming)
              .find(
                (resource) => resource.initiatorType === 'xmlhttprequest' && resource.name.endsWith(performanceKey),
              );
            if (entry != null) {
              Object.assign(eventMap, {
                perf_start_time: entry.startTime,
                perf_domain_lookup_start: entry.domainLookupStart,
                perf_domain_lookup_end: entry.domainLookupEnd,
                perf_connect_start: entry.connectStart,
                perf_secure_connection_start: entry.secureConnectionStart,
                perf_connect_end: entry.connectEnd,
                perf_request_start: entry.requestStart,
                perf_response_end: entry.responseEnd,
                perf_duration: entry.duration,
                perf_encoded_body_size: entry.encodedBodySize,
                perf_decoded_body_size: entry.decodedBodySize,
                perf_transfer_size: entry.transferSize,
              });
            }
          }
          segment.track(ChartEvent.ApiResponsed, eventMap);

          if (!data?.intervals) {
            if (![404, 429].includes(status)) {
              tradeLogger.warn('chart_data_missing', {
                error_message: 'Invalid response from getIntervalsApi',
                error_category: 'http_errors',
                symbol: symbolName,
                timeframe: secsToResolution(resolution),
                period,
                status,
                url: window.location.href,
              });
            } else {
              tradeLogger.info('chart_api_error', {
                error_message: 'Invalid response from getIntervalsApi',
                error_category: 'http_errors',
                symbol: symbolName,
                chart_resolution: secsToResolution(resolution),
                // status로 404(상장일 이전)와 429(rate limit)를 구분하기 위함
                status,
                period,
                url: window.location.href,
              });
            }
            return new Block(from, to, [], status);
          }

          const bars = data.intervals
            .map(apiIntervalDataToBar)
            .filter((bar) => bar.open != null && bar.time <= period.to * 1_000);
          return new Block(from, to, bars, status);
        },
        { symbol: symbolName, timeframe: resolutionString, period: { from, to }, direction },
      ),
    );

    if (direction === 'rtl') return Promise.all(blocks.reverse());
    return Promise.all(blocks);
  }
}

export function barTimestamp(bar: { time: number }) {
  return Math.floor(bar.time / 1_000);
}

interface TickResult {
  bar: Bar;
  isFinished: boolean;
}
type Tick = (lastBar: Bar) => TickResult;

export interface BarsUpdaterEvents {
  tick: Bar;
}

class BarsUpdater extends EventEmitter<BarsUpdaterEvents> {
  static readonly MaximumGapThatAvailableToCoverByIntervalSnapshot = 3;

  public lastBar: Bar | undefined;

  #lastTimestamp = Math.floor(NearWorldClock.now() / 1_000);
  #lastFinishedBar: Bar | undefined;
  #lastBarFinished: boolean = false;
  #onTick: ((bar: Bar, isFinished: boolean) => Promise<void>) | undefined;
  #tickQueue: Tick[] = [];
  #filling = false;
  #firstFetchFinished = false;

  #disposed = false;
  #abortController = new AbortController();

  #timeout = startTimeout(() => {
    if (typeof window === 'undefined') return;
    const hashSymbol = new URLSearchParams(window.location.hash.slice(1)).get('symbol');
    if (hashSymbol?.startsWith('dummy')) return;
    tradeLogger.warn('chart_ticker_not_delivered', {
      url: window.location.href,
      symbol: this.symbolName,
      last_insert_timestamp: this.#lastInsertTimestamp,
      last_update_timestamp: this.#lastUpdateTimestamp,
    });
  }, 10000);
  #lastInsertTimestamp: number | null = null;
  #lastUpdateTimestamp: number | null = null;

  constructor(
    private symbolName: string,
    /**
     * The resolution in seconds.
     */
    private resolution: number,
  ) {
    super();
    this.#timeout.reset();
  }

  get signal() {
    return this.#abortController.signal;
  }

  remainingBarFrom(timestamp: number) {
    return calcBarCount({ from: this.#lastTimestamp, to: timestamp }, this.resolution);
  }

  get remainingBarFromNow() {
    return this.remainingBarFrom(Math.floor(NearWorldClock.now() / 1000));
  }

  #updateLastBar(bar: Bar, isFinished: boolean) {
    if (this.lastBar && bar.time < this.lastBar.time) return;
    this.lastBar = bar;
    this.#lastBarFinished = isFinished;
    if (isFinished) this.#lastFinishedBar = bar;
    this.#lastTimestamp = barTimestamp(bar);
  }

  async #doTick(bar: Bar, isFinished: boolean) {
    if (!this.#onTick) return;
    try {
      if (this.#lastFinishedBar) {
        if (bar.time <= this.#lastFinishedBar.time) return;

        const gap = this.remainingBarFrom(barTimestamp(bar));
        if (gap === 1 && this.#lastFinishedBar.close !== bar.open) {
          const openCloseGap = Math.abs(this.#lastFinishedBar.close - bar.open);
          const priceGapPercent = (openCloseGap / Math.max(this.#lastFinishedBar.close, bar.open)) * 100;
          tradeLogger.warn('chart_data_discrepancy', {
            error_message: 'An attempt to concatenate bars with different prices has been detected',
            symbol: this.symbolName,
            error_timestamp: bar.time,
            timeframe: secsToResolution(this.resolution),
            diff_percent: priceGapPercent,
            lastBar: this.#lastFinishedBar,
            bar,
            url: window.location.href,
          });
        }
        if (gap > 1) {
          tradeLogger.warn('chart_data_missing', {
            error_message: 'An attempt to concatenate bars from different times has been detected',
            error_category: 'data_errors',
            symbol: this.symbolName,
            timeframe: secsToResolution(this.resolution),
            error_timestamp: bar.time,
            lastBar: this.#lastFinishedBar,
            gap,
            url: window.location.href,
          });
        }
      }

      /**
       * NOTE: TradingView mutates bar object directly.
       * TradingView always controls resolved symbol's timezone,
       * and it calculates time based on the timezone and set directly.
       * So, we should clone the bar object to prevent the mutation of the original object.
       */
      const clonedBar = cloneDeep(bar);
      await this.#onTick(clonedBar, isFinished);
      this.emit('tick', clonedBar);
    } finally {
      this.#updateLastBar(bar, isFinished);
    }
  }

  set onTick(value: ((bar: Bar, isFinished: boolean) => Promise<void>) | undefined) {
    this.#onTick = value;
  }

  #requestIntervalSnapshot: RequestIntervalSnapshotFn = () => {
    throw new Error('`requestIntervalSnapshot` must be set before calling it.');
  };

  get requestIntervalSnapshot(): RequestIntervalSnapshotFn {
    return this.#requestIntervalSnapshot;
  }

  set requestIntervalSnapshot(value: RequestIntervalSnapshotFn) {
    this.#requestIntervalSnapshot = value;
  }

  async fetch(period: Period) {
    try {
      const blocks = await Block.fetch(this.symbolName, this.resolution, period, 'rtl');
      const bars = Block.toBars(blocks);

      /**
       * NOTE: TradingView mutates bar object directly.
       * TradingView always controls resolved symbol's timezone,
       * and it calculates time based on the timezone and set directly.
       * So, we should clone the bar object to prevent the mutation of the original object.
       */
      const lastBar = cloneDeep(bars.at(-1));
      if (lastBar != null) this.#updateLastBar(lastBar, true);
      return { blocks, bars };
    } finally {
      if (!this.#firstFetchFinished) {
        try {
          const lastTick = this.#tickQueue.at(-1);
          if (lastTick != null && this.lastBar) {
            const lastBar = lastTick(this.lastBar);
            await this.#fillAndFlushTickQueue(barTimestamp(lastBar.bar));
          }
        } finally {
          this.#firstFetchFinished = true;
        }
      }
    }
  }

  async tick(time: number, tickFn: Tick) {
    if (this.#disposed) return;
    this.#lastInsertTimestamp = time;
    this.#lastUpdateTimestamp = Date.now();
    this.#timeout.reset();

    this.#tickQueue.push(tickFn);
    if (!this.#firstFetchFinished) return;
    await this.#fillAndFlushTickQueue(barTimestamp({ time }));
  }

  // 소켓에서 interval snapshot을 요청해서 빠르게 최근 봉 두개를 얻어오는 기능
  async fetchIntervalSnapshot() {
    const { symbolName, resolution } = this;
    const { symbol, priceType } = symbolNameToInfo(symbolName);
    // Snapshot stream shares the klines transport (spot MID / perp LAST|MARK).
    const intervalKey = `${symbol}:${toIntervalTransportPriceType(priceType)}:${secsToResolution(resolution)}`;
    try {
      const start = performance.now();
      const intervalDatas = await this.requestIntervalSnapshot(intervalKey);
      const end = performance.now();
      const durationNanoSeconds = (end - start) * 1_000_000;
      const params = {
        cpe_name: 'chart_interval_snapshot_brought',
        domain: 'trade',
        log_version: 1,
        symbol: symbolName,
        duration: durationNanoSeconds,
      } satisfies PerformanceEvent['perf_chart_interval_snapshot_brought'];
      tradeLogger.info('chart_interval_snapshot_brought', params);
      segment.track('perf_chart_interval_snapshot_brought', params);

      if (intervalDatas.length < 1) {
        tradeLogger.warn('chart_data_missing', {
          error_category: 'websocket_errors',
          error_message: 'No interval snapshot data fetched',
          symbol: symbolName,
          timeframe: secsToResolution(resolution),
          interval_snapshot_count: intervalDatas.length,
          url: window.location.href,
        });
      } else if (intervalDatas.at(-1)?.f) {
        // context: https://prextech.slack.com/archives/C081Z1PECA2/p1743052969655279
        tradeLogger.warn('chart_data_missing', {
          error_category: 'websocket_errors',
          error_message: 'Last interval snapshot bar is finished (should not be finished)',
          symbol: symbolName,
          timeframe: secsToResolution(resolution),
          url: window.location.href,
        });
      }

      for (const intervalData of intervalDatas) {
        const bar = socketIntervalDataToBar(intervalData);
        await this.#doTick(bar, Boolean(intervalData.f));
      }
    } catch (e) {
      if (e instanceof IntervalSnapshotSocketClosedError) {
        // interval snapshot 수행중 소켓이 닫혔다면
        // tick queue를 남겨놓을 경우 flush 과정에서 봉이 빠진 상태를 만들 수 있으므로
        // 소켓이 다시 연결되었을 때 복구 로직에서 다시 채워지도록 함
        this.#tickQueue.length = 0;
        tradeLogger.warn('chart_interval_snapshot_warn', {
          error_message: 'Interval snapshot socket closed',
          error_category: 'network_errors',
          symbol: symbolName,
          chart_resolution: secsToResolution(resolution),
        });
        return;
      }
      if (e instanceof IntervalSnapshotTimeoutError) {
        tradeLogger.warn('interval_snapshot_timeout', {
          error_message: 'Interval snapshot request timed out',
          error_category: 'network_errors',
          symbol: symbolName,
          timeframe: secsToResolution(resolution),
          url: window.location.href,
        });
        return;
      }
      datadog.addError(e, {
        error_message: 'Unknown error while fetch interval snapshot',
        symbol: symbolName,
        timeframe: secsToResolution(resolution),
        url: window.location.href,
      });
    }
  }

  async #flushTickQueue() {
    try {
      for (const tick of this.#tickQueue) {
        try {
          const tickResult = tick(this.lastBar!);
          await this.#doTick(tickResult.bar, tickResult.isFinished);
        } catch (err) {
          if (err instanceof AbortError) {
            return;
          }

          // handle update without insert
          await this.fetchIntervalSnapshot();
        }
      }
    } finally {
      this.#tickQueue.length = 0;
    }
  }

  async #fillAndFlushTickQueue(fillTo: number) {
    if (this.#filling) return;
    const { symbolName, resolution } = this;
    const period = { from: this.#lastTimestamp, to: fillTo };
    const gap = this.remainingBarFrom(fillTo);
    let blocks: Block[] = [];
    try {
      this.#filling = true;
      let shouldDoBlockFetch = false;
      let shouldDoIntervalSnapshot = false;
      determine: {
        // lastBar가 완성되지 않았는데 gap이 벌어졌다면 이빨이 빠지지 않았어도 lastBar 위치를 채워줘야 함
        if (this.lastBar && !this.#lastBarFinished && gap > 0) shouldDoIntervalSnapshot = true;
        // 최근에 리스팅된 심볼이라 klines에서 빈 응답이 온 경우, interval snapshot을 강제로 호출해서 채울 수 있는 봉은 채워줘야 함
        if (!this.lastBar) shouldDoIntervalSnapshot = true;
        // gap이 1보다 같거나 작으면 이빨이 빠지지 않은 것임
        if (gap <= 1) break determine;
        // gap이 2를 넘으면 이빨을 하나 채우는 것으로는 부족하므로 블록 fetch를 함
        if (gap >= 2) shouldDoBlockFetch = true;
        // gap이 1과 2 사이에 있는 경우
        if (gap > 1 && gap < 2) shouldDoIntervalSnapshot = true;
      }
      if (shouldDoBlockFetch) {
        blocks = await Block.fetch(symbolName, resolution, period, 'ltr');
        await Block.forEachBar(blocks, (bar) => this.#doTick(bar, true));
        // 블록 fetch api는 봉 단위만큼 캐시된 응답을 하기 때문에 최신 봉 하나가 여전히 누락될 수 있으므로
        // interval snapshot을 요청해서 최신 봉을 메움
        shouldDoIntervalSnapshot = true;
      }
      if (shouldDoIntervalSnapshot) {
        const beforeGap = this.remainingBarFrom(fillTo);
        if (beforeGap > BarsUpdater.MaximumGapThatAvailableToCoverByIntervalSnapshot) {
          tradeLogger.warn('chart_data_missing', {
            error_message: 'The gap between the last bar and the current time is too large to fill.',
            error_category: 'data_errors',
            symbol: symbolName,
            timeframe: secsToResolution(resolution),
            gap: beforeGap,
            url: window.location.href,
          });
        }
        // interval snapshot을 block fetch보다 뒤에 수행하는 이유는
        // tvchart에 봉을 채워줄 때 항상 시간 순으로 넣어줘야 하기 때문
        await this.fetchIntervalSnapshot();
      }
    } catch (error) {
      tradeLogger.warn(
        'chart_data_missing',
        {
          error_message: 'An error occurred while filling the bar.',
          error_category: 'runtime_errors',
          symbol: symbolName,
          timeframe: secsToResolution(resolution),
          url: window.location.href,
        },
        error,
      );
    } finally {
      await this.#flushTickQueue();
      this.#filling = false;
    }
  }

  dispose() {
    this.#disposed = true;
    this.#abortController.abort();
    this.onTick = undefined;
    this.#timeout.clear();
  }
}

function createKey(symbolName: string, resolution: number) {
  return `${symbolName}:${resolution}` as const;
}

const barsUpdaters: Record<`${string}:${number}`, BarsUpdater> = {};
const barWaiters: Record<`${string}:${number}`, Deferred<BarsUpdater>> = {};
export function getBarsUpdater(symbolName: string, resolution: number): BarsUpdater | undefined {
  const key = createKey(symbolName, resolution);
  const barsUpdater = barsUpdaters[key];
  if (barsUpdater) {
    barWaiters[key]?.resolve(barsUpdater);
  }
  return barsUpdater;
}

export function getOrCreateBarsUpdater(symbolName: string, resolution: number): BarsUpdater {
  const key = createKey(symbolName, resolution);
  barsUpdaters[key] ??= new BarsUpdater(symbolName, resolution);
  barWaiters[key]?.resolve(barsUpdaters[key]);
  return barsUpdaters[key];
}

export function disposeBarsUpdater(symbolName: string, resolution: number): void {
  const key = createKey(symbolName, resolution);
  if (!(key in barsUpdaters)) return;
  if (barWaiters[key]?.state === 'fulfilled') {
    delete barWaiters[key];
  }
  barsUpdaters[key].dispose();
  delete barsUpdaters[key];
}

export function waitBarsUpdater(symbolName: string, resolution: number): Promise<BarsUpdater> {
  const key = createKey(symbolName, resolution);
  barWaiters[key] ??= deferred();
  if (key in barsUpdaters) barWaiters[key].resolve(barsUpdaters[key]);
  return barWaiters[key];
}
